import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// THUNK
export const getUserDetails = createAsyncThunk(
    'users/getUser',
    async ({ accountId, userId, token, customerId, customerUserId }, { rejectWithValue }) => {
    try {
        // Caso 1: utente operativo standard della piattaforma (userId è valorizzato)
        if (userId) {
            const url = `${import.meta.env.VITE_API_BASE_URL}/users?accountId=${accountId}&userId=${userId}`;
            const headers = {
                'Content-Type': 'application/json',
                'auth-token': token
            };
            const options = { method: 'GET', headers };
            
            const response = await fetch(url, options);
            
            if (!response.ok) {
                const error = await response.json();
                console.error('getUserDetails (userSlice) - error:', error);
                return rejectWithValue({
                    message: error.message || `Errore sconosciuto.`
                });
            }
            const result = await response.json();
            return result;
        }
        // Caso 2: utente cliente (customerUserId e customerId sono valorizzati)
        else if (customerId && customerUserId) {
            
            // Costruiamo l'URL per recuperare i dati del customer
            const customerUrl = `${import.meta.env.VITE_API_BASE_URL}/customers?customerId=${customerId}&accountId=${accountId}`;
            const headers = {
                'Content-Type': 'application/json',
                'auth-token': token
            };
            const options = { method: 'GET', headers };
            
            // Facciamo la chiamata per recuperare i dati del cliente
            const customerResponse = await fetch(customerUrl, options);
            
            if (!customerResponse.ok) {
                const error = await customerResponse.json();
                console.error('getUserDetails (userSlice) - customer error:', error);
                return rejectWithValue({
                    message: error.message || `Errore nel recupero dei dati cliente.`
                });
            }
            
            // Estraiamo i dati del cliente dalla risposta
            const customerResult = await customerResponse.json();
            
            // Determiniamo dove si trovano i dati del cliente (direttamente nell'oggetto o in una proprietà 'data')
            let customer;
            if (customerResult.data) {
                customer = customerResult.data;
            } else if (customerResult._id) {
                // Se il cliente ha un _id, assumiamo che i dati siano direttamente nell'oggetto customerResult
                customer = customerResult;
            } else {
                return rejectWithValue({
                    message: 'Dati cliente non disponibili o in formato non riconosciuto.'
                });
            }
            
            // Determiniamo dove si trovano i contatti (contactsList o contacts)
            let contactsArray = [];
            if (customer.contactsList && Array.isArray(customer.contactsList) && customer.contactsList.length > 0) {
                contactsArray = customer.contactsList;
            } else if (customer.contacts && Array.isArray(customer.contacts) && customer.contacts.length > 0) {
                contactsArray = customer.contacts;
            }
            
            // Cerchiamo il contatto specifico con ID corrispondente a customerUserId
            const userContact = contactsArray.find(contact => {
                // Conversione sicura a stringa per confronto
                const contactId = String(contact._id || '');
                const targetId = String(customerUserId || '');
                return contactId === targetId;
            });
            
            if (!userContact) {
                return rejectWithValue({
                    message: 'Contatto cliente non trovato.'
                });
            }
            
            // Restituiamo un oggetto formattato come un utente ma con i dati del contatto cliente
            const userResult = {
                message: 'Dati utente cliente recuperati con successo',
                data: {
                    _id: userContact._id,
                    name: userContact.name,
                    surname: userContact.surname,
                    email: userContact.email,
                    customerId: customerId,
                    customerUserId: customerUserId,
                    isCustomerContact: true,  // Flag per identificare che è un contatto cliente
                    role: userContact.role || 'customer',
                    // Aggiungiamo altre informazioni utili dal customer
                    customer: {
                        company: customer.company,
                        name: customer.name,
                        surname: customer.surname,
                        addresses: customer.addresses
                    }
                }
            };
            
            return userResult;
        }
        // Caso 3: nessun ID valido fornito
        else {
            return rejectWithValue({
                message: 'Nessun identificativo utente valido fornito.'
            });
        }
    } catch (error) {
        console.error('getUserDetails (userSlice) - catch error:', error);
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

const initialState = {
    data: null,
    isLoading: false,
    error: null
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        clearUserData: (state) => {
            state.data = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUserDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getUserDetails.fulfilled, (state, action) => {
                state.data = action.payload;
                state.error = null
                state.isLoading = false;
            })
            .addCase(getUserDetails.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nel caricamento dei dati dell'account.";
                state.isLoading = false;
            })
    }
})

// Selector
export const selectUserData = (state) => state.user.data;
export const selectUserLoading = (state) => state.user.isLoading;
export const selectUserError = (state) => state.user.error;

export const { clearUserData } = userSlice.actions;
export default userSlice.reducer;
